import React, {useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {useFormik} from 'formik';
import {LANGUAGES} from "../../../mock/languages";
import {useNotification} from "../../../context/NotificationContext";
import {lang} from "../../../types/lang";
import {useQuery} from "react-query";
import {getQuestionById, getQuestionCategory, updateQuestion} from "../../../services/questionSvc";
import {EmptySection} from "../../../components/sections/empty/Empty";
import ReactQuill from "react-quill";
import {quillConfig} from "../../../mock/quill-config";
import {LoaderSection} from "../../../components/sections/loader/Loader";

export const QuestionUpdatePage = () => {
  const navigate = useNavigate();
  const {formatMessage} = useIntl();
  const {showNotification} = useNotification();
  const {data: questionCategory} = useQuery("getQuestionCategory", getQuestionCategory)
  const {questionId} = useParams();
  const {
    data: questionData,
    isLoading: questionIsLoading,
    isError
  } = useQuery(['getQuestionById', questionId], () => getQuestionById(Number(questionId)), {
    enabled: !!questionId,
    refetchOnWindowFocus: false,
  });

  const [activeTab, setActiveTab] = useState<lang>("uz");
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: {
        uz: questionData?.title_uz || '',
        ru: questionData?.title_ru || '',
        en: questionData?.title_en || '',
      },
      description: {
        uz: questionData?.description_uz || '',
        ru: questionData?.description_ru || '',
        en: questionData?.description_en || '',
      },
      category: questionData?.category.id || "",
    },
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append('title_uz', values.title.uz);
      formData.append('title_ru', values.title.ru);
      formData.append('title_en', values.title.en);
      formData.append('description_uz', values.description.uz);
      formData.append('description_ru', values.description.ru);
      formData.append('description_en', values.description.en)
      formData.append('category', values.category.toString());

      try {
        const res = await updateQuestion(questionId, formData);
        if (res?.success) {
          showNotification("Muvaffaqiyatli", "Savol-javob yangilandi", "success")
          setTimeout(() => {
            navigate(-1)
          }, 1000)
        }
      } catch (e) {
        showNotification("Xatolik!", "", "error")
      }
    },
  });

  const handleTabChange = (lang) => {
    setActiveTab(lang);
  };

  if (isError) return <EmptySection height={"50vh"}/>
  if (questionIsLoading) return <LoaderSection height={"50vh"}/>

  return (
      <div>
        <div className={"card"}>
          <div className="card-header">
            <h1 className="card-title">Savol-javob qo'shish</h1>
            <ul className="nav nav-tabs nav-line-tabs nav-stretch fs-6 border-0">
              {LANGUAGES.map((lang) => (
                  <li className="nav-item" key={lang.key}>
                    <button
                        className={`nav-link ${activeTab === lang.key ? 'active' : ''}`}
                        onClick={() => handleTabChange(lang.key)}
                    >
                      {lang.name}
                    </button>
                  </li>
              ))}
            </ul>
          </div>
          <div className="card-body">
            <div id="kt_account_profile_details" className="collapse show">
              <form
                  className="form"
                  onSubmit={formik.handleSubmit}
                  encType="multipart/form-data"
              >
                <div className="card-body p-9">
                  <div className="row mb-6">
                    <label className="col-lg-4 col-form-label required fw-bold fs-6">
                      Savol
                    </label>
                    <div className="col-lg-8 fv-row">
                    <textarea
                        className="form-control form-control-lg form-control-solid"
                        placeholder="Savol"
                        name={`title.${activeTab}`}
                        value={formik.values.title[activeTab]}
                        onChange={formik.handleChange}
                    />
                      {formik.touched.title?.[activeTab] && formik.errors.title?.[activeTab] && (
                          <div className="text-danger">{formik.errors.title[activeTab]}</div>
                      )}
                    </div>
                  </div>

                  <div className="row mb-6">
                    <label className="col-lg-4 col-form-label required fw-bold fs-6">
                      Javob
                    </label>
                    <div className="col-lg-8 fv-row">
                      <div className={"h-200px"}>
                        <ReactQuill
                            placeholder="Javob"
                            key={activeTab}
                            style={{
                              height: "150px",
                            }}
                            formats={quillConfig?.formats}
                            modules={quillConfig?.modules}
                            value={formik.values.description[activeTab]}
                            onChange={(value) =>
                                formik.setFieldValue(`description.${activeTab}`, value)
                            }
                        />
                      </div>
                      {formik.touched.description?.[activeTab] && formik.errors.description?.[activeTab] && (
                          <div className="text-danger">{formik.errors.description[activeTab]}</div>
                      )}
                    </div>
                  </div>

                  <div className="row mb-6">
                    <label className="col-lg-4 col-form-label required fw-bold fs-6">
                      Savol kategoriyasi
                    </label>
                    <div className="col-lg-8 fv-row">
                      <select
                          className="form-select form-select-lg form-select-solid"
                          name="category"
                          value={formik.values.category}
                          onChange={formik.handleChange}>
                        {
                          questionCategory?.map((category) => (
                              <option key={category.id} value={category.id}>{category.name}</option>
                          ))
                        }
                      </select>
                      {formik.touched.category && formik.errors.category && (
                          <div className="text-danger">{formik.errors.category}</div>
                      )}
                    </div>
                  </div>

                </div>
                <div className="card-footer">
                  <div className="justify-content-end d-flex gap-2">
                    <button
                        className="btn btn-secondary"
                        onClick={() => navigate(-1)}
                        type="button"
                    >
                      {formatMessage({id: "Bekor qilish"})}
                    </button>
                    <button
                        disabled={formik.isSubmitting}
                        className="btn btn-primary"
                        type="submit"
                    >
                      {
                        formik.isSubmitting ? "Saqlanmoqda..." : "Saqlash"
                      }
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
  );
};